<script lang="ts" generics="T extends string">
  // Segmented-toggle radio group. One pill per option; the selected one
  // gets the accent fill, unselected ones are transparent with a hover
  // hint. Built on real <input type="radio"> (visually hidden) so
  // keyboard nav (Arrow keys, Tab) and screen readers work as expected.
  //
  // Generic over option-value types so consumers get type-safe `value`
  // bindings — pass `'miles' | 'kilometers'` and the callback signature
  // narrows automatically.

  interface Option {
    value: T;
    label: string;
  }

  interface Props {
    /** Currently selected value. Use `bind:value` for two-way binding. */
    value: T;
    options: ReadonlyArray<Option>;
    /** Required so multiple groups on the same page don't share a name. */
    name: string;
    /** Optional accessible label for the whole group. */
    ariaLabel?: string;
    /** Whole-group disabled state — useful while a save is in flight. */
    disabled?: boolean;
    /** Fires after `value` updates, with the new value. Wires the
     *  optimistic-save pattern without forcing an extra `$effect`. */
    onChange?: (value: T) => void;
    /** Visual size — md is the default; sm is for compact contexts. */
    size?: 'sm' | 'md';
  }

  let {
    value = $bindable(),
    options,
    name,
    ariaLabel,
    disabled = false,
    onChange,
    size = 'md',
  }: Props = $props();

  function select(next: T): void {
    if (disabled) return;
    if (next === value) return;
    value = next;
    onChange?.(next);
  }
</script>

<div
  class="radio-group radio-group--{size}"
  class:radio-group--disabled={disabled}
  role="radiogroup"
  aria-label={ariaLabel}
>
  {#each options as opt (opt.value)}
    {@const selected = opt.value === value}
    <label
      class="radio-group__option"
      class:radio-group__option--selected={selected}
    >
      <input
        type="radio"
        class="radio-group__input"
        {name}
        value={opt.value}
        checked={selected}
        {disabled}
        onchange={() => select(opt.value)}
      />
      <span class="radio-group__label">{opt.label}</span>
    </label>
  {/each}
</div>
